import { DeleteOutline } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import {
  canDeleteLaborEntry,
  canEditLaborEntry,
  hasLaborEditAccess,
  Permissions,
  useScopeChecker,
} from 'src/rbac';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
  Avatar,
  Card,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import Edit02Icon from '@untitled-ui/icons-react/build/esm/Edit02';
import moment from 'moment';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LaborLocationTypeIcons } from 'src/pages/therapist-dashboard/dashboard-icon';
import { deleteLaborHoursAction } from 'src/redux/actions/laborHourActions';
import { convertDateToSelectedTimeZone } from 'src/utils/convertDateToSelectedTimeZone';
import { getInitials } from 'src/utils/get-initials';
import LoaderIcon from '../../../src/components/LoaderImg/loader.gif';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import ConfirmDeleteModal from './delete-confirmation-modal';
import { clockInActivityType } from 'src/config';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import useCurrentUser from 'src/hooks/use-current-user';
import { humanizeEnum } from '../labor-report-and-approval/labor-report-mapper';
// Approval state of a punch, as returned by the labour-management list. An entry nobody
// has acted on yet has no decision recorded, so it reads as Pending.
const LOCK_GOLD = '#E3A008';

const VALIDATION_CONFIG: Record<
  string,
  { label: string; bg: string; color: string; icon?: 'lock' | 'close' }
> = {
  APPROVED: { label: 'Approved', bg: '#DEF7EC', color: '#03543F', icon: 'lock' },
  NOT_APPROVED: { label: 'Not approved', bg: '#FEE2E2', color: '#B91C1C', icon: 'close' },
  PENDING: { label: 'Pending', bg: '#F1F5F9', color: '#475569' },
};

const ValidationChip = ({
  status,
  notApprovedReason,
}: {
  status?: string | null;
  notApprovedReason?: string | null;
}) => {
  const normalized = (status || '').toUpperCase();
  const config = VALIDATION_CONFIG[normalized] || VALIDATION_CONFIG.PENDING;

  const reason = normalized === 'NOT_APPROVED' ? humanizeEnum(notApprovedReason) : '';
  return (
    <Stack alignItems="flex-start">
      <Chip
        size="small"
        icon={
          config.icon === 'lock' ? (
            <LockOutlinedIcon sx={{ fontSize: 14, color: `${LOCK_GOLD} !important` }} />
          ) : config.icon === 'close' ? (
            <CloseIcon sx={{ fontSize: 14, color: `${config.color} !important` }} />
          ) : undefined
        }
        label={config.label}
        sx={{
          height: 22,
          fontSize: 12,
          fontWeight: 600,
          bgcolor: config.bg,
          color: config.color,
          borderRadius: '6px',
          '& .MuiChip-label': { px: 1 },
        }}
      />
      {reason ? (
        <Typography sx={{ mt: 0.5, fontSize: 12, color: config.color }}>
          Reason: {reason}
        </Typography>
      ) : null}
    </Stack>
  );
};

const LocationType: any = [
  {
    key: 'MILEAGE',
    name: 'Mileage',
  },
  {
    key: 'DRIVE',
    name: 'Drive',
  },
  {
    key: 'BUILDING',
    name: 'Building',
  },
];

type Props = {
  handleRequestLabor: any;
  handleAddNewData: any;
  onPageChange: any;
  onRowsPerPageChange: any;
  page: any;
  rowPerPage: any;
  openDeleteModal: any;
  setOpenDeleteModal: any;
};

const LaborHourManagementTable = (props: Props) => {
  const {
    handleRequestLabor,
    onRowsPerPageChange,
    page,
    rowPerPage,
    onPageChange,
    openDeleteModal,
    setOpenDeleteModal,
  } = props;

  const dispatch = useDispatch();
  const [providerId] = useCurrentUser(['providerId']);
  const [checkActivityType] = useFeatureToggle(clockInActivityType, 'ENTERPRISE');
  const getAllLaborHrs = useSelector((state: any) => state.getLaborHoursReducer);
  const getLaborCodesResponse = useSelector((state: any) => state.getLaborCodesReducer?.response);
  const isLaborValidationEnabled = useSelector(
    (state: any) => state?.getLaborValidationToggleReducer?.response?.active
  );
  const has = useScopeChecker();
  // Actions column is for users who can actually act on an entry.
  const canEditLabor = hasLaborEditAccess(has);
  // THIN-8096: Edit Own Labor only covers entries the signed-in provider owns, so the
  // per-row controls ask about the row rather than about the user alone. Same rule the
  // backend applies in LaborEditAccessPolicy.canEditEntry, so a rendered action is one
  // the API will accept. The `(you)` marker below already compares the same two ids.
  const isOwnEntry = (entry: any) =>
    Boolean(providerId) && entry?.therapistId === providerId;

  function calculateFutureTime(totalSec: number): string {
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    const hoursString = String(hours).padStart(2, '0');
    const minutesString = String(minutes).padStart(2, '0');

    const secondsString = String(seconds).padStart(2, '0');

    return `${hoursString}:${minutesString}:${secondsString}`;
  }

  const [editData, setEditData] = useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, lobar_data: any) => {
    setDeleteId(lobar_data?.labourManagementId);

    if (!lobar_data?.clockOut && lobar_data?.locationType !== 'MILEAGE') {
      const newData = { ...lobar_data, clockOut: lobar_data?.clockIn };
      setEditData(newData);
      return setAnchorEl(event.currentTarget);
    }
    setAnchorEl(event.currentTarget);
    setEditData(lobar_data);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [deleteId, setDeleteId] = useState('');

  const handleCloseInfo = () => {
    setOpenDeleteModal(false);
  };

  const confirmHandleDeleteModal = () => {
    dispatch(deleteLaborHoursAction(deleteId));
  };

  const isToday = (date: any) => {
    const today = new Date(convertDateToSelectedTimeZone(new Date()));
    const dateToCheck = new Date(convertDateToSelectedTimeZone(date));

    return (
      dateToCheck.getFullYear() === today.getFullYear() &&
      dateToCheck.getMonth() === today.getMonth() &&
      dateToCheck.getDate() === today.getDate()
    );
  };

  return (
    <>
      <Stack gap="1rem">
        <Card>
          <>
            {getAllLaborHrs?.loading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '45vh',
                }}
              >
                <img
                  src={LoaderIcon}
                  alt="loader.."
                />
              </div>
            ) : (
              <Box sx={{ minHeight: 500 }}>
                <Table sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      {has(Permissions.UPDATE_LABOR_MANAGEMENT) && checkActivityType ? (
                        <TableCell align="center">Activity Type</TableCell>
                      ) : null}

                      <TableCell>Clock in</TableCell>
                      <TableCell>Clock out</TableCell>
                      <TableCell>Total hours</TableCell>
                      <TableCell>Mileage</TableCell>
                      {isLaborValidationEnabled && <TableCell>Validation</TableCell>}

                      {canEditLabor ? <TableCell align="right"></TableCell> : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!!getAllLaborHrs?.response?.content?.length ? (
                      getAllLaborHrs?.response?.content?.map((lobar_data: any) => {
                        const laborCodeDisplayName =
                          lobar_data?.laborCode &&
                          getLaborCodesResponse?.find(
                            (code: any) => code?.key === lobar_data?.laborCode
                          )?.displayName;
                        return (
                          <TableRow
                            hover
                            key={lobar_data?.labourManagementId}
                          >
                            <TableCell>
                              {' '}
                              <Stack
                                alignItems="center"
                                direction="row"
                                gap="1rem"
                              >
                                <Avatar
                                  sx={{
                                    height: 42,
                                    width: 42,
                                  }}
                                >
                                  {getInitials(lobar_data?.therapistName)}
                                </Avatar>
                                <Typography variant="subtitle2">
                                  {lobar_data?.therapistName}
                                </Typography>
                                {/* Marks the signed-in user's own punch — the only row a
                                    therapist sees, and a useful anchor in the facility list. */}
                                {lobar_data?.therapistId === providerId && (
                                  <Typography
                                    color="text.secondary"
                                    variant="caption"
                                  >
                                    (you)
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {' '}
                              <Stack
                                flexDirection={'row'}
                                gap={1}
                              >
                                <LaborLocationTypeIcons iconType={lobar_data?.locationType} />{' '}
                                <Typography
                                  variant="body1"
                                  sx={{
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {' '}
                                  {
                                    LocationType?.find(
                                      (res: any) => res.key == lobar_data?.locationType
                                    )?.name
                                  }
                                </Typography>
                                {laborCodeDisplayName ? (
                                  <Chip
                                    label={`${laborCodeDisplayName}`}
                                    variant="outlined"
                                    sx={{ marginLeft: '10px' }}
                                  />
                                ) : null}
                              </Stack>
                            </TableCell>
                            {has(Permissions.UPDATE_LABOR_MANAGEMENT) && checkActivityType ? (
                              <TableCell align="center">
                                <Typography
                                  variant="body1"
                                  sx={{
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {lobar_data?.clockInActivityType?.charAt(0)?.toUpperCase() +
                                    lobar_data?.clockInActivityType?.slice(1)?.toLowerCase() || '—'}
                                </Typography>
                              </TableCell>
                            ) : null}

                            <TableCell>
                              {lobar_data?.clockIn
                                ? moment(
                                    new Date(convertDateToSelectedTimeZone(lobar_data?.clockIn))
                                  ).format('hh:mm A')
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {lobar_data?.clockOut
                                ? moment(
                                    new Date(convertDateToSelectedTimeZone(lobar_data?.clockOut))
                                  ).format('hh:mm A')
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {lobar_data?.totalSec
                                ? calculateFutureTime(lobar_data?.totalSec)
                                : '—'}
                            </TableCell>
                            <TableCell>{lobar_data?.mileage ? lobar_data?.mileage : '—'}</TableCell>
                            {isLaborValidationEnabled && (
                              <TableCell>
                                <ValidationChip
                                  status={lobar_data?.status}
                                  notApprovedReason={lobar_data?.notApprovedReason}
                                />
                              </TableCell>
                            )}
                            {canEditLabor ? (
                              <TableCell>
                                <Stack sx={{ marginRight: '10px' }}>
                                  <IconButton
                                    disabled={
                                      (!canEditLaborEntry(has, isOwnEntry(lobar_data)) &&
                                        !canDeleteLaborEntry(has, isOwnEntry(lobar_data))) ||
                                      (!lobar_data?.clockOut &&
                                        lobar_data?.locationType !== 'MILEAGE' &&
                                        isToday(lobar_data?.clockIn))
                                    }
                                    onClick={(e) => handleClick(e, lobar_data)}
                                  >
                                    <MoreVertRoundedIcon />
                                  </IconButton>
                                  <Menu
                                    id="basic-menu"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    MenuListProps={{
                                      'aria-labelledby': 'basic-button',
                                    }}
                                  >
                                    {canEditLaborEntry(has, isOwnEntry(editData)) && (
                                      <MenuItem
                                        sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
                                        onClick={() => {
                                          handleClose();
                                          handleRequestLabor(editData);
                                        }}
                                      >
                                        <Edit02Icon />
                                        Edit entry
                                      </MenuItem>
                                    )}

                                    {canDeleteLaborEntry(has, isOwnEntry(editData)) && (
                                      <MenuItem
                                        sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
                                        onClick={() => {
                                          handleClose();
                                          setOpenDeleteModal(true);
                                        }}
                                      >
                                        <DeleteOutline />
                                        Delete entry
                                      </MenuItem>
                                    )}
                                  </Menu>
                                </Stack>
                              </TableCell>
                            ) : null}
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={canEditLabor ? 9 : 8}>
                          <Box
                            textAlign="center"
                            p={4}
                          >
                            <Typography
                              color={'text.secondary'}
                              variant="subtitle2"
                            >
                              No Records Found
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
          <TablePagination
            component="div"
            count={getAllLaborHrs?.response?.totalElements}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={page}
            rowsPerPage={rowPerPage}
            rowsPerPageOptions={[10, 20, 30]}
          />
        </Card>
      </Stack>
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={handleCloseInfo}
        aria-labelledby="customized-dialog-title"
        open={openDeleteModal}
      >
        <ConfirmDeleteModal
          close={handleCloseInfo}
          handleDelete={confirmHandleDeleteModal}
        />
      </BootstrapDialog>
    </>
  );
};

export default LaborHourManagementTable;
